import { propertyFilters, propertySortOptions, propertyUnitOptions } from '../../../data/siteContent';
import PropertyCard from './PropertyCard';

function HomePropertyOverviewSection({
  activeFilter,
  activeType,
  availableTypes,
  canBrowseProperties,
  filteredProperties,
  goToNextPage,
  goToPreviousPage,
  handleFilterChange,
  handleSearchChange,
  handleSortChange,
  handleTypeChange,
  handleUnitsChange,
  isLoading,
  minUnits,
  portfolioReturnUrl,
  resetFilters,
  searchTerm,
  sortKey,
  visibleProperties,
}) {
  const hasActiveFilters =
    Boolean(searchTerm) || activeType !== 'alle' || minUnits > 0 || sortKey !== 'standard' || activeFilter !== 'alle';

  return (
    <section className="mx-auto w-[min(1120px,calc(100%-40px))] py-7" id="eiendommer">
      <div className="mb-6 grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] md:items-end">
        <div>
          <p className="mb-3.5 text-xs uppercase tracking-[0.16em] text-[#7c6b52]">Eiendomsoversikt</p>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-semibold leading-[1.02] text-[#163127]">
            Ledige boliger og bygg til leie.
          </h2>
        </div>
        <p className="m-0 text-[1.05rem] leading-[1.7] text-[#355046]">
          Søk på sted eller navn, filtrer på type og størrelse, og sorter listen. Åpne en eiendom for mer informasjon,
          bilder og kart.
        </p>
      </div>

      <div className="mb-6 grid gap-4 rounded-[24px] border border-[#18322812] bg-[rgba(255,255,255,0.44)] px-5 py-5">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#7c6b52]">Sok</span>
            <input
              className="rounded-full border border-[#1832281f] bg-white px-4 py-2.5 text-sm text-[#163127]"
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Sted eller navn"
              type="search"
              value={searchTerm}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#7c6b52]">Type</span>
            <select
              className="rounded-full border border-[#1832281f] bg-white px-4 py-2.5 text-sm text-[#163127]"
              onChange={(event) => handleTypeChange(event.target.value)}
              value={activeType}
            >
              <option value="alle">Alle typer</option>
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#7c6b52]">Storrelse</span>
            <select
              className="rounded-full border border-[#1832281f] bg-white px-4 py-2.5 text-sm text-[#163127]"
              onChange={(event) => handleUnitsChange(event.target.value)}
              value={String(minUnits)}
            >
              {propertyUnitOptions.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#7c6b52]">Sorter</span>
            <select
              className="rounded-full border border-[#1832281f] bg-white px-4 py-2.5 text-sm text-[#163127]"
              onChange={(event) => handleSortChange(event.target.value)}
              value={sortKey}
            >
              {propertySortOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#18322812] pt-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-3">
            {propertyFilters.map((filter) => {
              const isActive = filter.key === activeFilter;

              return (
                <button
                  className={
                    isActive
                      ? 'rounded-full bg-[#1d4c3b] px-4 py-2 text-sm font-semibold text-[#f9f5ee]'
                      : 'rounded-full border border-[#1832281f] bg-[rgba(249,245,238,0.78)] px-4 py-2 text-sm font-semibold text-[#355046]'
                  }
                  key={filter.key}
                  onClick={() => handleFilterChange(filter.key)}
                  type="button"
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-[#355046]">
            <span>
              Viser {visibleProperties.length} av {filteredProperties.length} boliger
            </span>
            {hasActiveFilters ? (
              <button
                className="rounded-full border border-[#1832281f] bg-white px-4 py-2 text-sm font-semibold text-[#355046]"
                onClick={resetFilters}
                type="button"
              >
                Nullstill
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="relative">
        <button
          aria-label="Vis forrige bolig"
          className="absolute left-0 top-[110px] z-10 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border border-[#1832281f] bg-[rgba(249,245,238,0.96)] text-xl text-[#163127] shadow-[0_12px_28px_rgba(24,50,40,0.12)] disabled:cursor-default disabled:opacity-45 max-md:left-3 max-md:translate-x-0"
          disabled={!canBrowseProperties}
          onClick={goToPreviousPage}
          type="button"
        >
          &lt;
        </button>
        <button
          aria-label="Vis neste bolig"
          className="absolute right-0 top-[110px] z-10 flex h-12 w-12 translate-x-1/2 items-center justify-center rounded-full border border-[#1832281f] bg-[rgba(249,245,238,0.96)] text-xl text-[#163127] shadow-[0_12px_28px_rgba(24,50,40,0.12)] disabled:cursor-default disabled:opacity-45 max-md:right-3 max-md:translate-x-0"
          disabled={!canBrowseProperties}
          onClick={goToNextPage}
          type="button"
        >
          &gt;
        </button>
        <div className="grid gap-5 md:grid-cols-3">
          {(isLoading ? [] : visibleProperties).map((property) => (
            <PropertyCard key={property.slug} portfolioReturnUrl={portfolioReturnUrl} property={property} />
          ))}
        </div>
        {!isLoading && visibleProperties.length === 0 ? (
          <div className="rounded-[24px] border border-[#18322812] bg-[rgba(255,255,255,0.44)] px-5 py-6 text-[#355046]">
            Ingen boliger matcher søket eller filtrene akkurat nå.
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default HomePropertyOverviewSection;